/**
 * Photo upload types for presigned URL generation and upload flow.
 */
import { z } from 'zod';

/**
 * Allowed content types for photo uploads.
 */
export const ALLOWED_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
] as const;

export type AllowedContentType = (typeof ALLOWED_CONTENT_TYPES)[number];

/**
 * Maximum number of photos allowed per order.
 */
export const MAX_PHOTOS = 10;

/**
 * Presigned URL expiry time in seconds (1 hour).
 */
export const UPLOAD_URL_EXPIRY_SECONDS = 3600;

/**
 * Request schema for getting presigned upload URLs.
 */
export const uploadUrlRequestSchema = z.object({
  files: z
    .array(
      z.object({
        filename: z.string().min(1).max(255),
        contentType: z.enum(ALLOWED_CONTENT_TYPES),
      })
    )
    .min(1)
    .max(MAX_PHOTOS),
  /**
   * Starting index for bucket path numbering.
   * Use this to prevent overwrites when uploading photos in batches.
   * E.g., if user has 2 photos and uploads 3 more, pass startIndex=2.
   */
  startIndex: z.number().int().min(0).max(MAX_PHOTOS).optional().default(0),
  /**
   * BIPA biometric consent version accepted by the user.
   * Required before photo upload — validated server-side against BIPA_CONSENT_VERSION.
   * Sent in the request body (rather than read from DB) to avoid race conditions
   * where the consent checkbox was toggled but auto-save hasn't persisted yet.
   */
  biometricConsentVersion: z.string().min(1),
});

export type UploadUrlRequest = z.infer<typeof uploadUrlRequestSchema>;

/**
 * Single upload URL response item.
 */
export interface UploadUrlItem {
  index: number;
  uploadUrl: string;
  bucketPath: string;
  expiresAt: string;
}

/**
 * Response from the upload URLs endpoint.
 */
export interface UploadUrlResponse {
  uploads: UploadUrlItem[];
}

/**
 * Error response from upload endpoints.
 */
export interface UploadApiError {
  error: string;
}

/**
 * Photo reference stored in survey data (after upload).
 * This format is compatible with the AI agent's photoProcessor.ts.
 */
export interface PhotoReference {
  bucketPath: string;
  caption?: string;
}
